import { PlanningNoteAnalysisResponse } from '~/clients/derived-api-types'
import { Surgery } from '~/storm'
import { isScheduledSurgery } from '~/utils/dips'
import { isNullish } from '~/utils/guards'
import { getSurgeryMetadata } from '~/utils/surgeryMetadata'

import { AvailabilityPeriod, isSameAvailabilityPeriod } from './suggestionFormatting'

export type TagAction = 'add' | 'remove'

export type TagData = {
    id: keyof PlanningNoteAnalysisResponse
    label: string
    action: TagAction
}

function hasSameAvailabilityPeriod(period: AvailabilityPeriod, periods: AvailabilityPeriod[]): boolean {
    return periods.some(currentPeriod => isSameAvailabilityPeriod(period, currentPeriod))
}

function hasDifferentAvailabilityPeriods(suggested: AvailabilityPeriod[], current: AvailabilityPeriod[]): boolean {
    if (suggested.length !== current.length) return true
    return suggested.some(period => !hasSameAvailabilityPeriod(period, current))
}

function getBooleanTagAction(suggested: boolean | null | undefined, current: boolean | null | undefined): TagAction | null {
    if (isNullish(suggested)) return null
    if (suggested === current) return null
    return suggested ? 'add' : 'remove'
}

export function hasNewSuggestions({ suggestions, surgery }: { suggestions: Partial<PlanningNoteAnalysisResponse>; surgery: Surgery }): boolean {
    const surgeryMetadata = getSurgeryMetadata(surgery)

    // Booleans: review needed when a suggestion exists and differs from current value.
    if (getBooleanTagAction(suggestions.patient_ready, surgeryMetadata?.patient_ready) !== null) return true
    if (getBooleanTagAction(suggestions.patient_prioritized, surgeryMetadata?.patient_prioritized) !== null) return true
    if (getBooleanTagAction(suggestions.patient_short_notice, surgeryMetadata?.patient_short_notice) !== null) return true
    if (isScheduledSurgery(surgery) && getBooleanTagAction(suggestions.patient_confirmed, surgeryMetadata?.patient_confirmed) !== null) return true

    const currentIsWaiting = surgeryMetadata?.availability_periods?.some(period => period.type === 'waiting') ?? null
    if (getBooleanTagAction(suggestions.is_waiting, currentIsWaiting) !== null) return true

    if (getBooleanTagAction(suggestions.self_declaration_form, surgeryMetadata?.self_declaration_form) !== null) return true
    if (getBooleanTagAction(suggestions.external_document, surgeryMetadata?.external_document) !== null) return true
    if (getBooleanTagAction(suggestions.test_results, surgeryMetadata?.test_results) !== null) return true

    // Patient availability: review needed when suggested periods differ from current periods.
    const suggestionPeriods = (suggestions.availability_periods ?? []) as AvailabilityPeriod[]
    const currentPeriods = (surgeryMetadata?.availability_periods ?? []) as AvailabilityPeriod[]
    if (suggestionPeriods.length > 0 && hasDifferentAvailabilityPeriods(suggestionPeriods, currentPeriods)) return true

    return false
}
